import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, Button, Input, notification, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";

import {
  deleteOnpremise,
  OnpremiseDuplicateError,
  OnpremiseValidationError,
  updateOnpremise,
} from "../api/onpremise";
import { userStore } from "../stores/userStore";
import { usePageTitle } from "../utils/usePageTitle";
import { usePendingAction } from "../utils/usePendingAction";

import "./OnPremisePage.css";

export const OnPremisePage = observer(function OnPremisePage() {
  const { t } = useTranslation();
  usePageTitle(t("menu.onpremise"));

  const plan = userStore.subscription?.plan ?? "lemon";
  const isLoaded = !!userStore.subscription;

  const [guid, setGuid] = useState(() => userStore.onpremiseGuid ?? "");
  const [saveAlert, setSaveAlert] = useState<"saved" | "unbound" | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [savingAction, setSavingAction] = useState<"bind" | "unbind" | null>(
    null,
  );
  const { pending: saving, run: runSave } = usePendingAction();

  const handleSubmit = async () => {
    if (!guid.trim()) {
      setInputError(t("onpremise.identifierRequired"));
      return;
    }
    await runSave(async () => {
      setSavingAction("bind");
      try {
        setSaveAlert(null);
        await updateOnpremise(guid);
        userStore.setOnpremiseGuid(guid);
        setSaveAlert("saved");
      } catch (err) {
        if (err instanceof OnpremiseValidationError)
          setInputError(t("onpremise.invalidGuid"));
        else if (err instanceof OnpremiseDuplicateError)
          setInputError(t("onpremise.guidInUse"));
        else notification.error({ message: t("errors.shared.common_error") });
      } finally {
        setSavingAction(null);
      }
    });
  };

  const handleUnbind = async () => {
    await runSave(async () => {
      setSavingAction("unbind");
      try {
        setSaveAlert(null);
        await deleteOnpremise();
        userStore.setOnpremiseGuid(null);
        setGuid("");
        setSaveAlert("unbound");
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      } finally {
        setSavingAction(null);
      }
    });
  };

  return (
    <div className="page onpremise-page" onClick={() => setSaveAlert(null)}>
      <h1>{t("menu.onpremise")}</h1>

      {!isLoaded ? (
        <div className="onpremise-page__center">
          <Spin size="large" />
        </div>
      ) : plan === "pomelo" ? (
        <>
          <div
            dangerouslySetInnerHTML={{ __html: t("onpremise.description") }}
          />
          <div className="onpremise-page__form">
            <Input
              value={guid}
              onChange={(e) => {
                setGuid(e.target.value);
                setSaveAlert(null);
                setInputError(null);
              }}
              placeholder={t("onpremise.identifier")}
              status={inputError ? "error" : undefined}
            />
            {inputError && (
              <Typography.Text type="danger">{inputError}</Typography.Text>
            )}
            {saveAlert && (
              <Alert
                type="success"
                title={t(
                  saveAlert === "saved"
                    ? "onpremise.saved"
                    : "onpremise.unbound",
                )}
                showIcon
                closable={{ afterClose: () => setSaveAlert(null) }}
                styles={{
                  root: {
                    background:
                      "color-mix(in srgb, var(--ng-green-color) 12%, white)",
                    borderColor:
                      "color-mix(in srgb, var(--ng-green-color) 35%, white)",
                  },
                  icon: { color: "var(--ng-green-color)" },
                }}
              />
            )}
            <div className="onpremise-page__actions">
              <Button
                type="primary"
                loading={savingAction === "bind"}
                disabled={saving && savingAction !== "bind"}
                onClick={handleSubmit}
              >
                {t("onpremise.bind")}
              </Button>
              {userStore.onpremiseGuid && (
                <Button
                  danger
                  loading={savingAction === "unbind"}
                  disabled={saving && savingAction !== "unbind"}
                  onClick={handleUnbind}
                >
                  {t("onpremise.unbind")}
                </Button>
              )}
            </div>
          </div>
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          description={
            <Trans
              i18nKey="onpremise.onpremiseIsNotAvailable"
              components={{ lnk: <Link to="/support" /> }}
            />
          }
        />
      )}
    </div>
  );
});
